import {useWindowSize} from '@wandb/weave/common/hooks/useWindowSize';
import {makePropsAreEqual} from '@wandb/weave/common/util/shouldUpdate';
import _ from 'lodash';
import React, {memo, useEffect, useMemo, useState} from 'react';

import {TABLE_MIN_COLUMN_WIDTH} from '../../util/constants';
import * as TableCols from '../../util/tablecols';
import * as WBTreeHelpers from '../../util/wbtree';
import {
  WBTableColumn,
  WBTableGridContainerProps,
  WBTableHoverCellCoords,
} from './types';
import {WBTableGrid} from './WBTableGrid';

const WBTableGridContainerComp: React.FC<WBTableGridContainerProps> = ({
  addFilter,
  addGroup,
  childrenPaginationMap,
  columns,
  columnDragAccessor,
  columnDropAccessor,
  columnResizingAccessor,
  expandable,
  expanded,
  expandedRowAddresses,
  fixedColumns,
  hideColumn,
  replaceColumnAggregation,
  columnKeySupportsAggregates,
  isSingleMode,
  isInReport,
  loading,
  moveColumn,
  rows,
  openSortPopup,
  pinnedTreeRef,
  readOnly,
  scrollXBounds,
  setChildrenPagination,
  setTableSettings,
  setTableState,
  showArtifactCounts,
  showLogCounts,
  SortIndicatorComponent,
  tableSettings,
  toggleExpandedRow,
  togglePinnedColumn,
  topLevelQueryVariables,
  unpinnedTreeRef,
  updateSort,
  updateTruncationType,
  truncationType,
  sort,
}: WBTableGridContainerProps) => {
  const windowSize = useWindowSize();
  const [hoverCellCoords, setHoverCellCoords] =
    useState<WBTableHoverCellCoords>([]);

  const pinnedColumns = useMemo(
    () => columns.filter(c => TableCols.isPinned(tableSettings, c.accessor)),
    [tableSettings, columns]
  );

  const unPinnedColumns = useMemo(
    () => columns.filter(c => !TableCols.isPinned(tableSettings, c.accessor)),
    [tableSettings, columns]
  );

  const pinnedColumnsWidth = _.sum(
    pinnedColumns.map(c => WBTreeHelpers.getColumnWidth(tableSettings, c.key))
  );

  // allow pinned columns to take up to 70% of available screen space.
  // if exceeded, automatically scale all pinned columns down to fit.
  // to prevent unnecessary re-renders, make sure to work with whole numbers.
  const pinnedColumnsWidthLimit = Math.floor(windowSize.width * 0.7);
  const pinnedColumnsMinWidth = pinnedColumns.length * TABLE_MIN_COLUMN_WIDTH;

  useEffect(() => {
    if (
      pinnedColumnsWidth > pinnedColumnsWidthLimit &&
      pinnedColumnsWidthLimit > pinnedColumnsMinWidth
    ) {
      const reducedColumnWidths = {...tableSettings.columnWidths};
      pinnedColumns.forEach(column => {
        const curColumnWidth = WBTreeHelpers.getColumnWidth(
          tableSettings,
          column.key
        );
        const percentage = curColumnWidth / pinnedColumnsWidth;
        reducedColumnWidths[column.accessor] = Math.floor(
          Math.max(pinnedColumnsWidthLimit * percentage, TABLE_MIN_COLUMN_WIDTH)
        );
      });
      setTableSettings({
        ...tableSettings,
        columnWidths: reducedColumnWidths,
      });
    }
  }, [
    pinnedColumns,
    pinnedColumnsMinWidth,
    pinnedColumnsWidth,
    pinnedColumnsWidthLimit,
    setTableSettings,
    tableSettings,
  ]);

  // Create a wrapper function so we can quickly render a pinned and unpinned table
  const renderTableBody = (opts: {
    cols: WBTableColumn[];
    pinned: boolean;
    leftMargin?: number;
  }) => {
    return (
      <WBTableGrid
        topLevelQueryVariables={topLevelQueryVariables}
        treeRef={opts.pinned ? pinnedTreeRef : unpinnedTreeRef}
        loadingTable={loading}
        childrenPaginationMap={childrenPaginationMap}
        setChildrenPagination={setChildrenPagination}
        pinned={opts.pinned}
        leftMargin={opts.leftMargin || 0}
        columns={opts.cols}
        fixedColumns={fixedColumns}
        rows={rows}
        updateSort={updateSort}
        updateTruncationType={updateTruncationType}
        hoverCellCoords={hoverCellCoords}
        tableSettings={tableSettings}
        SortIndicatorComponent={SortIndicatorComponent}
        setTableSettings={setTableSettings}
        setTableState={setTableState}
        setHoverCellCoords={setHoverCellCoords}
        addFilter={addFilter}
        addGroup={addGroup}
        columnDragAccessor={columnDragAccessor}
        columnDropAccessor={columnDropAccessor}
        columnResizingAccessor={columnResizingAccessor}
        expandable={expandable}
        expanded={expanded}
        isSingleMode={isSingleMode}
        isInReport={isInReport}
        showArtifactCounts={showArtifactCounts}
        showLogCounts={showLogCounts}
        readOnly={readOnly}
        moveColumn={moveColumn}
        togglePinnedColumn={togglePinnedColumn}
        hideColumn={hideColumn}
        replaceColumnAggregation={replaceColumnAggregation}
        columnKeySupportsAggregates={columnKeySupportsAggregates}
        expandedRowAddresses={expandedRowAddresses}
        toggleExpandedRow={toggleExpandedRow}
        scrollXBounds={opts.pinned ? [0, 10000] : scrollXBounds}
        openSortPopup={openSortPopup}
        truncationType={truncationType}
        sort={sort}
      />
    );
  };

  // When screen size is below tablet breakpoint we don't provide pinned columns behavior.
  // Render the table with normal inline scroll.
  if (windowSize.responsiveSize === 'small') {
    return renderTableBody({
      cols: columns,
      pinned: false,
    });
  } else {
    // Otherwise render the table with pinned columns on top
    return (
      <>
        {renderTableBody({
          cols: pinnedColumns,
          pinned: true,
        })}
        {(expandable == null || expanded) &&
          renderTableBody({
            cols: unPinnedColumns,
            pinned: false,
            leftMargin: pinnedColumnsWidth,
          })}
      </>
    );
  }
};

export const WBTableGridContainer = memo(
  WBTableGridContainerComp,
  makePropsAreEqual({
    name: 'WBTableGridContainerMemo',
    deep: [
      'query',
      'scrollXBounds',
      'groupSelection',
      'groupTempSelection',
      'tempSelectedCount',
      'fixedColumns',
      'columns',
      'topLevelQueryVariables',
      'tableSettings',
      'sort',
    ],
    ignoreFunctions: true,
  })
);
